import { Scroll, Preload, ScrollControls } from '@react-three/drei'
import '../styles.css'
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import { MathJaxContext } from 'better-react-mathjax';
import { MathJaxProvider, MathJaxFormula } from 'mathjax3-react';
import React, {useEffect} from 'react';
import Markdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import 'katex/dist/katex.min.css' // `rehype-katex` does not import the CSS for you
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter'
import {dark, vscDarkPlus} from 'react-syntax-highlighter/dist/esm/styles/prism'
import BlogLightbox from "./BlogLightbox";
import TableOfContent from "./TableOfContent";

export default function BlogPostIBVS() {
    const [openFig1, setOpenFig1] = React.useState(false);
    const [openFig2, setOpenFig2] = React.useState(false);
    const [openFig3, setOpenFig3] = React.useState(false);
    const [openFig4, setOpenFig4] = React.useState(false);
    const [openFig5, setOpenFig5] = React.useState(false);
    const [openFig6, setOpenFig6] = React.useState(false);
    const [openFig7, setOpenFig7] = React.useState(false);
    const [openFig8, setOpenFig8] = React.useState(false);
    const [openFig9, setOpenFig9] = React.useState(false);
    const [openFig10, setOpenFig10] = React.useState(false);
    const [openFig11, setOpenFig11] = React.useState(false);
    const contentRef = React.useRef(null);

const markdown1 = `
## Introduction

This article is a rigorous summary of articles, book chapters and tutorials I have read 
while studying the topic of Image-Based Visual Servo. You can find the resources at the end of the article. 
As in my previous articles, I am not going to site in a “academic” manner and 
try to reformulate sentences to sound different of what smart people have already 
formulated very well. Instead, I am going to shout out François Chaumette, 
Seth Hutchinson and Peter Corke right in the beginning and will not claim any 
novelty in the writing. My contribution lies in the simplicity of this light 
weight introduction and some practical aspects of implementing such controllers.

Visual servo control refers to the use of computer vision data to control the 
motion of a robot. The vision data may be acquired from a camera that is mounted 
directly on a robot manipulator or on a mobile robot, or the camera can be fixed 
in the workspace so that it can observe the robot motion from a stationary 
configuration.  There are two fundamentally different approaches to visual 
servo control: **Position-Based Visual Servo** (**PBVS**) and **Image-Based 
Visual Servo** (**IBVS**). 

- **PBVS** uses observed visual features, a calibrated camera and a known 
geometric model of the target to determine the pose of the target w.r.t. the 
camera. The robot then moves toward that pose and the control is performed in 
task space which is commonly $SE(3)$.
- **IBVS** on the other hand, omits the pose estimation step, and uses the 
image feature directly. The control is performed in image coordinate space 
$\\mathbb{R}^2$. The desired camera pose w.r.t. the target is defined 
implicitly by the image feature values at the goal pose.

This article will focus on **IBVS** in the so-called **eye-in-hand** case, 
where the camera is mounted on the manipulator, typically on the flange
of the robot. This is what I was dealing with, when getting into this topic.
`
const markdown2 = `
## Image-Based Visual Servo in the Eye-In-Hand Case

The eye-in-hand configuration assumes that the camera is rigidly mounted on the 
robot's end-effector and moves with the robot. Most commonly it is mechanically 
fixes to a flange or a tool. Figure 1. depicts this configuration and summarizes
the IBVS control-loop for this set up. 
`

const fig1Descp = `
Figure 1. Overview of the IBVS control-loop for the eye-in-hand case.
The IBVS controller computes the camera velocities $\\boldsymbol{\\nu}_c=$
based on the error $\\bold{e}(t)$ between the target features $\\bold{s}^*$ and the current
features $\\bold{s}(\\bold{m}(t),\\bold{a})$. The computed velocity is fed into
the robots joint controller and the current features are extracted from 
the most recent image in the new camera pose.
`

const markdown3 = `
The error $\\bold{e}(t)$, which is minimized by the IBVS controller is computed 
as the difference of the desired values of $k$ visual features 
$\\bold{s}^*\\in\\mathbb{R}^k$ and the vector of $k$ visual features 
$\\bold{s}(\\bold{m}(t),\\bold{a})\\in\\mathbb{R}^k$ computed from the image. 
The set of image measurements $\\bold{m}(t)$ can be for example the pixel 
coordinates of interest points, while the set of parameters $\\bold{a}$ 
represent additional knowledge about the system (e.g. coarse camera intrinsic 
parameters). 

The IBVS controller computes the spatial velocity 
$\\boldsymbol{\\nu}_c=(\\bold{v}_c,\\boldsymbol{\\omega}_c) \\in \\mathbb{R}^6$ 
of the camera, where $\\bold{v}_c\\in\\mathbb{R}^3$ denotes the instantaneous 
linear velocity of the origin of the camera frame and 
$\\boldsymbol{\\omega}_c\\in\\mathbb{R}^3$ the instantaneous angular velocity 
of the camera frame. This velocity is typically represented in the camera 
frame: Its the cameras velocity in the camera frame. 

The output of the IBVS controller is fed into the joint controller, which 
moves the robot joints such that it achieves the given camera velocity. 
Simultaneously, the feature extractor computes the current visual features 
$\\bold{s}(\\bold{m}(t),\\bold{a})$ from the captured camera frames, which are 
used for the error computation. 

For the following derivations we additional will assume a purely kinematic 
system (the dynamics of the camera motion are not considered), static 
targets and that the imaging geometry can be modelled as a pinhole camera 
model (perspective projection). Further, we consider first the case of 
controlling the motion of a camera with six degrees of freedom (6 DOF), 
e.g., a camera attached to the end effector of a six degree-of-freedom arm.
`

const notes1 = `
- In robotics literature, when the velocity is represented in its own frame, it is referred to as **body velocity**, not **spatial velocity**. The visual servoing community however looses this term loosly.
- **Instantaneous velocity** means the velocity at one exact moment in time. Mathematically, it is the derivative of a position with respect to time:
    
    $$
    v(t) = \\frac{d x(t)}{dt}=\\dot{x}(t).
    $$
    
    In contrast, the average velocity describes the overall change in position over a finite time interval, divided by the duration of that interval:
    
    $$
    v(t) = \\frac{\\Delta x}{\\Delta t}=\\bar{v}(t).
    $$
`

const markdown4 = `
## Designing the Control Law

The goal of all vision-based control schemes is to minimize an error 
$\\bold{e}(t)$, which is defined by

$$
\\bold{e}(t)=\\bold{s}(\\bold{m}(t),\\bold{a})-\\bold{s}^*.
$$

Let’s assume that we have already selected a set of visual features 
$\\bold{s}$. The typical approach is to design the IBVS controller as a 
velocity controller. To do this, we require the relationship between 
the time variation of $\\bold{s}$ and the camera velocity $\\boldsymbol{\\nu}_c$. 
This relationship is given by

$$
\\dot{\\bold{s}}=\\bold{L}_s\\boldsymbol{\\nu}_c,
$$

in which $\\bold{L}_e\\in\\mathbb{R}^{k\\times6}$ is named the **interaction 
matrix** related to $\\bold{s}$. The terms feature Jacobian or image Jacobian 
are also used interchangeably in the visual servo literature. 

Using the previous equations, we obtain the relationship between camera velocity 
and the time variation of the error as

$$
\\dot{\\bold{e}}=\\frac{d}{dt}\\bold{s}(t)-\\frac{d}{dt}\\bold{s}^*=\\dot{\\bold{s}}=\\bold{L}_e\\boldsymbol{\nu}_c,

$$

where $\\bold{L}_s=\\bold{L}_e$. Considering $\\boldsymbol{\\nu}_c$ as the 
input of the robot controller, we now want to find the controller which 
decreases the error. If we want to ensure an exponential decoupled 
decrease of the error (i.e., $\\dot{\\bold{e}}=-\\lambda\\bold{e}$), we get

$$
\\bold{L}_e\\boldsymbol{\\nu}_c=-\\lambda\\bold{e}(t),
$$

which we can solve in a least squares sense to obtain

$$
\\boldsymbol{\\nu}_c=-\\lambda\\bold{L}_e^{+}\\bold{e}(t),
$$

where $\\bold{L}_e^{+}\\in\\mathbb{R}^{6\\times k}$ is chosen as the 
Moore-Penrose pseudo-inverse of $\\bold{L}_e$, that is 

$$
\\bold{L}_e^{+}=\\left(\\bold{L}_e^{\\top}\\bold{L}_e\\right)^{-1}\\bold{L}_e^{\\top}
$$

when $\\bold{L}_e$ is of full rank six. This choice allows 
$||\\dot{\\bold{e}}-\\lambda\\bold{L}_e\\bold{L}_e^{+}\\bold{e}||$ and 
$||\\boldsymbol{\\nu}_c||$ to be minimal. In real visual servo systems, it 
is impossible to know perfectly in practice either $\\bold{L}_e$ or 
$\\bold{L}_e^{+}$, so an approximation or an estimation of one of these 
two matrices must be realized.  

In the sequel, we denote both the pseudoinverse of the approximation of 
the interaction matrix and the approximation of the pseudoinverse of the 
interaction matrix by the symbol $\\widehat{\\bold{L}_e^{+}}$. Using this 
notation, the control law becomes:

$$
\\boldsymbol{\\nu}_c=-\\lambda\\widehat{\\bold{L}_e^{+}}\\bold{e}(t).
$$

This is the basic design implemented by most visual servo controller. In 
the remainder of this article we will fill the details on how to chose 
$\\bold{s}$, how to define $\\bold{L}_s$ and how estimate $\\widehat{\\bold{L}_e^{+}}$.
`

const markdown5 = `
## Image-Based Visual Servoing with Point Features

Traditional image-based control schemes use the image-plane coordinates 
of a set of points to define the set $\\bold{s}$. The  image measurements 
$\\bold{m}$ are usually the pixel coordinates of the set of image points, 
and the parameters $\\bold{a}$ in the definition of $\\bold{s}(\\bold{m}(t),\\bold{a})$ 
are the camera intrinsic parameters to go from image measurements expressed 
in pixels to the features. For example, if the image measurements $\\bold{m}$ 
consist the pixel coordinates $u$ and $v$ and of $n$ image points and we 
have the camera intrinsics $\\bold{a}$ consisting of the focal length $f$ 
and the pixel sizes $s_x$and $s_y$, we can define the feature vector as

$$
\\bold{s}= \\left[\\begin{matrix}
\\widehat{x}_{I,1}(t)\\\\
\\widehat{y}_{I,1}(t)\\\\
\\vdots\\\\
\\widehat{x}_{I,n}(t)\\\\
\\widehat{y}_{I,n}(t)\\\\
\\end{matrix}\\right]\\in\\mathbb{R}^k,
$$

where $k=2n$ and $\\widehat{x}_{I,i}$ and $\\widehat{y}_{I,i}$ denote the 
normalized image plane coordinates of the $i$-th image point. We can 
derive the interaction matrix for this case by using perspective projection. 
Lets first consider a single image point. The image plane coordinates 
$(x_I,y_I)$ of this 3D point $\\bm{p}_c=(x_c,y_c, z_c)$ are given as

$$
\\begin{align*}
x_I &= f\\frac{x_c}{z_c}\\
y_I &= f\\frac{y_c}{z_c},
\\end{align*}
$$

where $f$ is the focal length in metric units. Given the pixel size 
$s_x$ and $s_y$ and the principal point offset $c_x$ and $c_y$, we can 
obtain the normalized image coordinates $(\\widehat{x}_I,\\widehat{y}_I)$ 
(in units of meters, so $f=1\\text{m}$, and w.r.t. the camera’s principal 
point) by

$$
\\begin{align*}
\\widehat{x}_I &= [1\\text{m}]\\frac{x_c}{z_c} =\\frac{u-c_x}{p_x}\\
\\widehat{y}_I &= [1\\text{m}]\\frac{y_c}{z_c} =\\frac{v-c_y}{p_y},
\\end{align*}
$$

where $p_x=f/s_x$ and $p_y=f/s_y$ are defined as the ration between the 
respective focal length $f$ and pixel size $s$.
`

const notes2 = `
Using the equations for the intrinsic transformation, we can rewrite the pixel coordinate $u$ as 

$$
\\begin{align*}
u &= m_xx_I+c_x
\\\\
&=\\frac{1}{s_x}x_I + c_x
\\\\
&=\\frac{1}{s_x}f\\frac{x_c}{z_c} + c_x
\\\\
&=p_x\\frac{x_c}{z_c} + c_x.
\\end{align*}
$$

Once we reorder the previous equation as

$$
\\frac{u-c_x}{p_x}=\\frac{x_c}{z_c},
$$

and the normalized coordinates equation as

$$
\\begin{align*}
\\widehat{x}_I &= [1\\text{m}] \\frac{x_c}{z_c}
\\\\
\\frac{1}{[1\\text{m}]}\\widehat{x}_I &= \\frac{x_c}{z_c},
\\end{align*}
$$

we can combine both and obtain

$$
\\widehat{x}_I = [1\\text{m}] \\frac{x_c}{z_c} =\\frac{u-c_x}{p_x}.
$$

 Similarly, we can derive it for $\\widehat{y}_I$.
`

const markdown6 = `
Using the quotient rule, we can now compute the temporal derivate for the 
normalized coordinates as

$$
\\dot{\\widehat{x}}_I =\\frac{\\dot{x}_cz_c - x_c\\dot{z_c}}{z_c^{2}},\\quad \\dot{\\widehat{y}}_I =\\frac{\\dot{y}_cz_c - y_c\\dot{z_c}}{z_c^{2}}.
$$

Substituting $x_c = \\widehat{x}_I/{z_c} [1\\text{m}]$ and $y_c = \\widehat{y}_I/{z_c} [1\\text{m}]$, we get 

$$
\\dot{\\widehat{x}}_I =\\frac{\\dot{x}_c}{z_c}-{\\widehat{x}}_I\\frac{\\dot{z}_c}{z_c}
,\\quad 
\\dot{\\widehat{y}}_I =\\frac{\\dot{y}_c}{z_c}-{\\widehat{y}}_I\\frac{\\dot{z}_c}{z_c},
$$

where we omit the metric unit $[1\\text{m}]$ from now on for simplicity. 

Now that we have derived the temporal derivative of the normalized image 
plane coordinates $(\\widehat{x}_I,\\widehat{y}_I)$ w.r.t. its camera frame 
coordinates $\\bm{p}_c$, we can finally obtain the interaction matrix by 
considering the kinematics in the camera frame. Consider a camera moving with 
a velocity $\\boldsymbol{\\nu}_c=(\\bold{v}_c,\\boldsymbol{\\omega}_c)$ and 
observing a point with camera relative coordinates $\\bm{p}_c=(x_c,y_c,z_c)$. 
The velocity of the point relative to the camera frame is

$$
\\dot{\\mathbf{p}}_{c}=-\\boldsymbol{\\omega}_{c}\\times\\mathbf{p}_{c}-\\mathbf{v}_c,
$$

which we can write in scalar form as

$$
\\begin{align*}
\\dot{x}_{c}&=y_{c}\\omega_{z,c}-z_{c}\\omega_{y,c}-v_{x}\\\\
\\dot{y}_{c}&=z_{c}\\omega_{x,c}-x_{c}\\omega_{z,c}-v_{y}\\\\
\\dot{z}_{c}&=x_{c}\\omega_{y,c}-y_{c}\\omega_{x,c}-v_{z}.
\\end{align*}
$$

In combination with the previous equations, we obtain in matrix form

$$
\\begin{bmatrix} \\dot{\\widehat{x}}_I \\\\\\dot{\\widehat{y}}_I\\end{bmatrix} = {\\begin{bmatrix}
-{1}/{z_c} & 0 & {{\\widehat{x}}_I}/{z_c} & \\widehat{x}_I\\widehat{y}_I & -(1+\\widehat{x}_I^2) & \\widehat{y}_I \\\\
0   & -{1}/{z_c}     &  \\widehat{y}_I/{z_c} & 1+\\widehat{y}_I^2 & -\\widehat{x}_I\\widehat{y}_I & -\\widehat{x}_I\\\\
\\end{bmatrix}} \\begin{bmatrix} v_x \\\\v_y \\\\v_z\\\\\\omega_x\\\\\\omega_y\\\\\\omega_z \\end{bmatrix}.
$$

We can write this more concise as

$$
\\dot{\\bold{s}}=\\bold{L}_{i,s}\\boldsymbol{\\nu}_c,
$$

where the $\\bold{L}_{i,s}\\in\\mathbb{R}^{2\\times 6}$ denotes interaction 
matrix for a single point feature $i$. Since the interaction contains the 
depth $z_c$ of the point relative to the camera frame, any control scheme 
that uses this form of the interaction matrix must estimate or approximate 
the value of $z_c$. Similarly, the camera intrinsics involved in the 
computation of the normalized image plane coordinates have to be estimated. 
Thus, in practice only an estimation $\\widehat{\\bold{L}}_s$ of $\\bold{L}_s$ 
can be used. 

To control the six DOF, at least three points are necessary, but for practical 
reasons, more than three points are usually considered. The interaction 
matrix for $n$ points is obtained by merely stacking the individual interaction 
matrices as

$$
\\bold{L}_s={\\begin{bmatrix}
\\bold{L}_{1,s} \\\\
\\vdots \\\\
\\bold{L}_{n,s}
\\end{bmatrix}}\\in\\mathbb{R}^{k\\times 6},
$$

with $k=2n$. The full relationship

$$
\\dot{\\bold{s}}=\\bold{L}_s\\boldsymbol{\\nu}_c
$$

for $n$ image points is hence given by

$$
\\begin{bmatrix} \\dot{\\widehat{x}}_{1,I} \\\\
\\dot{\\widehat{y}}_{1,I} \\\\
\\vdots \\\\
\\dot{\\widehat{x}}_{n,I} \\\\
\\dot{\\widehat{y}}_{n,I} \\\\
\\end{bmatrix} = {\\begin{bmatrix}
\\bold{L}_{1,s} \\\\
\\vdots \\\\
\\bold{L}_{n,s}
\\end{bmatrix}} \\begin{bmatrix} v_x \\\\v_y \\\\v_z\\\\\\omega_x\\\\\\omega_y\\\\\\omega_z \\end{bmatrix}.
$$

Now we can estimate $\\widehat{\\bold{L}_e^{+}}$ to be used in the control 
law with the derived interaction matrix $\\bold{L}_s$. The three popular 
choices are:

- $\\widehat{\\bold{L}_e^{+}}={\\bold{L}_s^{+}}$: The most obvious one, which 
requires the current depth of each point
- $\\widehat{\\bold{L}_e^{+}}={\\bold{L}_{s^*}^{+}}$: Requires only the values 
of the features for the desired position
- $\\widehat{\\bold{L}_e^{+}}=1/2({\\bold{L}_s}+{\\bold{L}_{s^*}})^{+}$: 
The mean of the previous approaches, provides a good performance in practice
`

const markdown7 = `
## Constraining the DOFs for IBVS

When solving practical use cases, it can be beneficial to impose a priori 
constraints to the controller to improve its performance. 

Consider the case, where you want the IBVS controller to only output 
velocities in certain directions, e.g. linear velocities in the $x$ and 
$y$ direction in the end effector frame and angular velocities around the 
$z$ axis of the end effector frame. 

To design such a controller, we first need to express the camera velocity 
in the end effector frame as

$$
\\boldsymbol{\\nu}_c :={^\\text{C}}\\boldsymbol{\\nu}_c= {^\\text{C}}{\\bold V}_\\text{E} {^\\text{E}}\\boldsymbol{\\nu}_c,
$$

where

$$
{^\\text{C}}{\\mathbf V}_\\text{E} = \\left[\\begin{array}{cc}  {^\\text{C}}{\\mathbf R}_\\text{E} &  {[{^\\text{C}}{\\mathbf t}_\\text{E}}]_\\times \\; {^C}{\\mathbf R}_\\text{E} \\\\  0_{3\\times3} & {^C}{\\mathbf R}_\\text{E}  \\end{array}  \\right] \\;
$$

is the velocity twist matrix corresponding to the transformation matrix 
from the end effector frame $\\text{E}$ to the camera frame $C$ 

$$
{^C}{\\mathbf T}_\\text{E} = \\left[\\begin{array}{cc}  {^C}{\\mathbf R}_\\text{E} &  {{^C}{\\mathbf t}_\\text{E}} \\\\  0 & 1 \\end{array}  \\right] \\;
$$

and $^\\text{E}\\boldsymbol{\\nu}_c$ is the camera velocity represented in the end effector frame.  

The relationship between the time variation of the vector of $k$ visual 
features $\\dot{\\bold{s}}$ and the camera velocity $\\boldsymbol{\\nu}_c$ 
can then be written as

$$
\\begin{align*}
\\dot{\\mathbf{s}}&=\\mathbf{L}_s{\\boldsymbol{\\nu}}_c\\\\
&=\\mathbf{L}_s  {^C}{\\mathbf V}_\\text{E} {^\\text{E}}{\\boldsymbol{\\nu}}_c.
\\end{align*}
$$

We can force the IBVS controller to only control $v_x, v_y, v_z$ and 
$\\omega_z$, while $\\omega_x$ and $\\omega_y$ always reamin 0, by introducing 
a selection matrix $\\mathbf{S}$:

$$
\\mathbf{S}=\\begin{bmatrix}
1 & 0 & 0 & 0 \\\\
0 & 1 & 0 & 0 \\\\
0 & 0 & 1 & 0 \\\\
0 & 0 & 0 & 0 \\\\
0 & 0 & 0 & 0 \\\\
0 & 0 & 0 & 1 \\\\
\\end{bmatrix}\\in\\mathbb{R}^{6\\times4}
$$

The relationship between $\\dot{\\mathbf{s}}$ and the camera velocity in 
the end effector frame $\\boldsymbol{\\nu}_c$ is then modified to

$$
\\begin{align*}
\\dot{\\mathbf{s}}
&=\\mathbf{L}_s  {^\\text{C}}{\\mathbf V}_\\text{E} \\mathbf{S} {^\\text{E}}{\\boldsymbol{\\nu}}_c^* \\\\
&=\\mathbf{L}_s^*{^\\text{E}}{\\boldsymbol{\\nu}}_c^*
\\end{align*}
$$

where

$$
\\mathbf{L}_s^*=\\mathbf{L}_s  {^\\text{C}}{\\mathbf V}_\\text{E} \\mathbf{S} \\in\\mathbb{R}^{k\\times4}
$$

is the modified interaction matrix for point features and $\\boldsymbol{\\nu}_c^*$ is the modified 
camera velocity vector consisting only of the velocities which are controlled 
by the IBVS controller:

$$
{^\\text{E}}{\\boldsymbol{\\nu}}_c^*:=\\begin{bmatrix}
v_x \\\\
v_y \\\\
v_z \\\\
\\omega_z \\\\
\\end{bmatrix}\\in\\mathbb{R}^{4}.
$$

The final control law is then

$$
{^\\text{E}}{\\boldsymbol{\\nu}}_c^*=-\\lambda(\\mathbf{L}_s^*)^+\\mathbf{e}.
$$

By not considering $\\omega_x$ and $\\omega_y$ in the control law, the 
IBVS controller is forced to minimize the error by only utilizing the 
remaining four DOFs.
`

const markdownLiterature = `
## References
`
const components = {
  greyblock: ({ children }) => (
    <div style={{ color: "#777" }}>{children}</div>
  ),
};

    useEffect(() => {
        if (!window.MathJax) {
            window.MathJax = {
            tex: {
    inlineMath: [["$", "$"]],
    displayMath: [["$$", "$$"]],
            },
            chtml: {
                linebreaks: {
                automatic: true,
                width: "container",
                },
            },
            };
        }

        window.MathJax?.typesetPromise?.();

        const onResize = () => {
            window.MathJax?.typesetClear?.();
            window.MathJax?.typesetPromise?.();
        };

        window.addEventListener("resize", onResize);

        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (    
    <div className='article-row'>
        <div className="article-card">
            <h1 className="article-title">Understanding Image-Based Visual Servo</h1>
            <hr className="article-hr"/>
            <div className="article-post-layout">
                <aside className="article-toc">
                    <TableOfContent 
                        contentRef={contentRef} 
                        selector="h2, h3" 
                    />
                </aside>
                <div className="article-post-content">
                    <div className="article-text">
                        <div className="md" ref={contentRef}>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown1}
                    </Markdown>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown2}
                    </Markdown>
                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                        <img
                            src="https://d3rnpbgh9zefuh.cloudfront.net/IBVS/IBVS_Overview.png"
                            alt="SIFT scale-space illustration"
                            style={{ 
                                width: '100%', 
                                maxWidth: '1000px',
                                cursor: "zoom-in"
                            }}
                            onClick={() => setOpenFig1(true)}
                        />
                        <figcaption 
                            style={{
                                marginTop: '1px',
                                fontSize: '0.9em',
                                color: '#555',
                                maxWidth: '1000px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                textAlign: 'left'
                            }}
                        >
                            <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                {fig1Descp}
                            </Markdown>
                        </figcaption>
                        <BlogLightbox
                            open={openFig1}
                            close={() => setOpenFig1(false)}
                            src="https://d3rnpbgh9zefuh.cloudfront.net/IBVS/IBVS_Overview.png"
                        />
                    </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown3}
                    </Markdown>
                    <details>
                        <summary className="gray-summary">
                            Notes
                        </summary>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {notes1}
                        </Markdown>
                    </details>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown4}
                    </Markdown>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown5}
                    </Markdown>
                    <details>
                        <summary className="gray-summary">
                            Derivation of the Previous Equations
                        </summary>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {notes2}
                        </Markdown>
                    </details>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown6}
                    </Markdown>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown7}
                    </Markdown>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdownLiterature}
                    </Markdown>
                    <div className="gray-summary">
                        <p>
                            F. Cahumette, S, Hutchninson; Visual Servo Control Part I: Basic Approaches;
                            Article in IEEE Robotics & Automation Magazine; 2007
                        </p>
                        <p>
                            F. Cahumette, S. Hutchninson; Visual Servo Control Part II: Advanced Approaches;
                            Article in IEEE Robotics & Automation Magazine; 2007
                        </p>
                        <p>
                            F. Chaumette, S. Hutchninson, P. Corke; Handbook of Robotics; 2th ed.
                            Springer, 2016; Chapter 34; Visual Servoing.
                        </p>
                        <p>
                            P. Corke; Robotics, Vision and Control; 1th ed.
                            Springer, 2013; Chapter 15; Vision-Based Control
                        </p>
                        <p>
                            Visual Servoing Platform Documentation; Tutorial: Image-based visual servo (IBVS); {" "}
                            <a
                            href="https://visp-doc.inria.fr/doxygen/visp-daily/tutorial-ibvs.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            >
                             https://visp-doc.inria.fr/doxygen/visp-daily/tutorial-ibvs.html
                            </a>
                            ; accessed May. 2026.
                        </p>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
}
